import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { ApiError } from '../api';
import { LEAD_TONE, groupBookingEndpoints } from '../api-group-bookings';
import type { LeadListRow, Lookups } from '../api-group-bookings';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars } from './event/shared';

/**
 * Group sales: the pipeline (D-376).
 *
 * Every inquiry and every call about a group is a lead. This page is the list
 * staff work from — what is due today first, by status and owner — and where a
 * phone call becomes a lead. The lead's own page is where it is quoted, held
 * and booked.
 */

export default function GroupSalesPage() {
  const can = usePermissions();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [mine, setMine] = useState(false);
  const [due, setDue] = useState(false);
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const lookups = useQuery<Lookups, ApiError>({ queryKey: ['group-lookups'], queryFn: groupBookingEndpoints.lookups });
  const leads = useQuery({
    queryKey: ['group-leads', status, mine, due, open, q],
    queryFn: () => groupBookingEndpoints.leads({ ...(status ? { status } : {}), ...(mine ? { owner: 'me' } : {}), ...(due ? { due: 'today' as const } : {}), open: open && !status, ...(q.trim() ? { q: q.trim() } : {}) }),
  });

  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;

  const rows = leads.data?.leads ?? [];
  const today = leads.data?.today ?? lookups.data?.today ?? '';
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Group sales</h1>
          <p className="text-sm text-ink-500">Inquiries from the website and the phone, quoted, held and booked.</p>
        </div>
        <div className="flex gap-2">
          {can('group_sales.manage') ? (
            <Button variant="primary" onClick={() => setAdding(!adding)}>
              {adding ? 'Close' : 'Log a lead'}
            </Button>
          ) : null}
        </div>
      </div>

      {adding && lookups.data ? <NewLead lookups={lookups.data} onDone={(id) => navigate(`/group-sales/leads/${id}`)} /> : null}

      <div className="flex flex-wrap gap-2">
        {(lookups.data?.statuses ?? []).map((s) => (
          <button
            key={s.code}
            type="button"
            onClick={() => setStatus(status === s.code ? '' : s.code)}
            className={`rounded border px-3 py-1.5 text-sm ${status === s.code ? 'border-accent bg-accent-soft' : 'border-line bg-surface'}`}
          >
            {s.name}
            {!status && counts.get(s.code) ? <span className="ml-2 tabular text-ink-500">{counts.get(s.code)}</span> : null}
          </button>
        ))}
      </div>

      <Panel
        title={due ? `Due today (${today})` : 'Leads'}
        actions={
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={due} onChange={(e) => setDue(e.target.checked)} /> Due today or overdue
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Mine
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} /> Open only
            </label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, group, email or GRP-…" aria-label="Search leads" className={`w-56 ${INPUT}`} />
          </div>
        }
      >
        {leads.error ? <ErrorNote>{(leads.error as ApiError).message}</ErrorNote> : null}
        <Table headers={['Lead', 'Group', 'Status', 'Wants', 'Follow up', 'Owner', 'Booked']}>
          {rows.length === 0 ? (
            <EmptyRow columns={7}>{leads.isLoading ? 'Loading…' : 'No leads here.'}</EmptyRow>
          ) : (
            rows.map((row) => <LeadRow key={row.id} row={row} today={today} />)
          )}
        </Table>
      </Panel>
    </div>
  );
}

function LeadRow({ row, today }: { row: LeadListRow; today: string }) {
  const overdue = row.next_follow_up_on !== null && row.next_follow_up_on < today;
  const dueToday = row.next_follow_up_on === today;
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-5 py-3 text-sm">
        <Link to={`/group-sales/leads/${row.id}`} className="font-medium text-live hover:underline">
          {row.contact_name}
        </Link>
        <div className="font-mono text-xs text-ink-500">{row.reference}</div>
      </td>
      <td className="px-5 py-3 text-sm">
        {row.organization_name ?? '—'}
        <div className="text-xs text-ink-500">
          {row.group_type_name ?? ''}
          {row.headcount ? ` · ${row.headcount} people` : ''}
        </div>
      </td>
      <td className="px-5 py-3 text-sm">
        <Badge tone={LEAD_TONE[row.status]}>{row.status_name}</Badge>
      </td>
      <td className="px-5 py-3 text-sm">
        {row.interest_name ?? 'Not sure'}
        {row.games ? <div className="text-xs text-ink-500">{row.games}</div> : null}
      </td>
      <td className="px-5 py-3 text-sm">
        {row.next_follow_up_on ?? '—'}
        {overdue ? <div><Badge tone="bad">overdue</Badge></div> : dueToday ? <div><Badge tone="warn">today</Badge></div> : null}
      </td>
      <td className="px-5 py-3 text-sm">{row.owner_name ?? <span className="text-ink-400">nobody</span>}</td>
      <td className="tabular px-5 py-3 text-sm">{row.booked_cents > 0 ? dollars(row.booked_cents) : '—'}</td>
    </tr>
  );
}

/** A lead from a phone call, an email or somebody at the window. */
function NewLead({ lookups, onDone }: { lookups: Lookups; onDone: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ contact_name: '', contact_email: '', contact_phone: '', organization_name: '', group_type: 'BUSINESS', headcount: '', source: 'PHONE', message: '', next_follow_up_on: lookups.today });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value });
  const save = useMutation({
    mutationFn: () =>
      groupBookingEndpoints.createLead({
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        organization_name: form.organization_name.trim() || null,
        group_type: form.group_type || null,
        headcount: form.headcount ? Number(form.headcount) : null,
        source: form.source,
        message: form.message.trim() || null,
        next_follow_up_on: form.next_follow_up_on || null,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['group-leads'] });
      onDone(result.lead.id);
    },
  });
  return (
    <Panel title="Log a lead" description="Somebody called, emailed or asked at the window. You are its owner; change that on its page.">
      <form
        className="grid gap-3 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.error ? <div className="sm:col-span-2 lg:col-span-4"><ErrorNote>{(save.error as ApiError).message}</ErrorNote></div> : null}
        <Field label="Name" htmlFor="gl-name">
          <input id="gl-name" required value={form.contact_name} onChange={set('contact_name')} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Email" htmlFor="gl-email">
          <input id="gl-email" type="email" value={form.contact_email} onChange={set('contact_email')} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Phone" htmlFor="gl-phone">
          <input id="gl-phone" value={form.contact_phone} onChange={set('contact_phone')} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Organization" htmlFor="gl-org">
          <input id="gl-org" value={form.organization_name} onChange={set('organization_name')} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Kind of group" htmlFor="gl-type">
          <select id="gl-type" value={form.group_type} onChange={set('group_type')} className={`w-full ${INPUT}`}>
            {lookups.group_types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="How many" htmlFor="gl-count">
          <input id="gl-count" inputMode="numeric" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value.replace(/\D/g, '') })} className={`w-full ${INPUT}`} />
        </Field>
        <Field label="Came from" htmlFor="gl-source">
          <select id="gl-source" value={form.source} onChange={set('source')} className={`w-full ${INPUT}`}>
            {lookups.sources.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Follow up on" htmlFor="gl-follow">
          <input id="gl-follow" type="date" value={form.next_follow_up_on} onChange={set('next_follow_up_on')} className={`w-full ${INPUT}`} />
        </Field>
        <div className="sm:col-span-2 lg:col-span-4">
          <Field label="What they said" htmlFor="gl-message">
            <textarea id="gl-message" rows={2} value={form.message} onChange={set('message')} className={`w-full ${INPUT}`} />
          </Field>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Button type="submit" variant="primary" disabled={save.isPending || !form.contact_name.trim() || (!form.contact_email.trim() && !form.contact_phone.trim())}>
            {save.isPending ? 'Saving…' : 'Save the lead'}
          </Button>
          <span className="ml-3 text-xs text-ink-500">Logged {formatWhen(new Date().toISOString())}</span>
        </div>
      </form>
    </Panel>
  );
}
